import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export type ColumnFormatType = "number" | "currency" | "date";

export type ColumnFormat = {
  type: ColumnFormatType;
  /** Predefined preset id, or `custom` to use `pattern`. */
  preset?: string;
  /** Custom date pattern (dayjs tokens) or number pattern (`0`/`#`/`,`/`.`). */
  pattern?: string;
  /** ISO 4217 code when currency preset is custom. */
  currency?: string;
  /** BCP 47 locale override. */
  locale?: string;
};

export type ColumnFormatPreset = {
  id: string;
  type: ColumnFormatType;
  labelKey: string;
  /** Example shown in the editor. */
  example?: string;
  locale?: string;
  currency?: string;
  datePattern?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  style?: "decimal" | "percent" | "currency";
  notation?: "standard" | "compact";
};

export const COLUMN_FORMAT_PRESETS: ColumnFormatPreset[] = [
  { id: "integer", type: "number", labelKey: "columnFormat.preset.integer", example: "1.234", maximumFractionDigits: 0 },
  { id: "decimal2", type: "number", labelKey: "columnFormat.preset.decimal2", example: "1.234,56", minimumFractionDigits: 2, maximumFractionDigits: 2 },
  { id: "decimal3", type: "number", labelKey: "columnFormat.preset.decimal3", example: "1.234,567", minimumFractionDigits: 3, maximumFractionDigits: 3 },
  { id: "percent", type: "number", labelKey: "columnFormat.preset.percent", example: "%12,3", style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 },
  { id: "compact", type: "number", labelKey: "columnFormat.preset.compact", example: "1,2B", notation: "compact", maximumFractionDigits: 1 },
  { id: "try", type: "currency", labelKey: "columnFormat.preset.try", example: "₺1.234,56", locale: "tr-TR", currency: "TRY" },
  { id: "usd", type: "currency", labelKey: "columnFormat.preset.usd", example: "$1,234.56", locale: "en-US", currency: "USD" },
  { id: "eur", type: "currency", labelKey: "columnFormat.preset.eur", example: "1.234,56 €", locale: "de-DE", currency: "EUR" },
  { id: "gbp", type: "currency", labelKey: "columnFormat.preset.gbp", example: "£1,234.56", locale: "en-GB", currency: "GBP" },
  { id: "isoDate", type: "date", labelKey: "columnFormat.preset.isoDate", example: "2024-06-15", datePattern: "YYYY-MM-DD" },
  { id: "shortDate", type: "date", labelKey: "columnFormat.preset.shortDate", example: "15.06.2024", datePattern: "DD.MM.YYYY" },
  { id: "dateTime", type: "date", labelKey: "columnFormat.preset.dateTime", example: "15.06.2024 14:30", datePattern: "DD.MM.YYYY HH:mm" },
  { id: "time", type: "date", labelKey: "columnFormat.preset.time", example: "14:30", datePattern: "HH:mm" },
  { id: "longDate", type: "date", labelKey: "columnFormat.preset.longDate", example: "15 June 2024", datePattern: "D MMMM YYYY" },
];

export const CUSTOM_FORMAT_PRESET_ID = "custom";
export const FORMAT_OPTION_NONE = "none";

export type FormatOptionGroup = "none" | ColumnFormatType | "custom";

export function formatToOptionKey(format: ColumnFormat | undefined): string {
  if (!format) return FORMAT_OPTION_NONE;
  if (format.preset === CUSTOM_FORMAT_PRESET_ID) {
    return `custom:${format.type}`;
  }
  const preset = format.preset || presetsForType(format.type)[0]?.id;
  return preset ? `${format.type}:${preset}` : FORMAT_OPTION_NONE;
}

export function optionKeyToFormat(key: string, previous?: ColumnFormat): ColumnFormat | undefined {
  if (!key || key === FORMAT_OPTION_NONE) return undefined;
  if (key.startsWith("custom:")) {
    const type = key.slice("custom:".length) as ColumnFormatType;
    return {
      type,
      preset: CUSTOM_FORMAT_PRESET_ID,
      pattern:
        previous?.type === type && previous.pattern
          ? previous.pattern
          : type === "date"
            ? "DD.MM.YYYY"
            : "#,##0.00",
      ...(type === "currency" ? { currency: previous?.currency || "TRY" } : {}),
    };
  }
  const sep = key.indexOf(":");
  if (sep <= 0) return undefined;
  const type = key.slice(0, sep) as ColumnFormatType;
  const preset = key.slice(sep + 1);
  if (type !== "number" && type !== "currency" && type !== "date") return undefined;
  return { type, preset };
}

export function formatOptionGroups(): Array<{ groupKey: string; type: FormatOptionGroup; presetIds: string[] }> {
  return [
    { groupKey: "columnFormat.group.none", type: "none", presetIds: [] },
    { groupKey: "columnFormat.group.number", type: "number", presetIds: presetsForType("number").map((p) => p.id) },
    { groupKey: "columnFormat.group.currency", type: "currency", presetIds: presetsForType("currency").map((p) => p.id) },
    { groupKey: "columnFormat.group.date", type: "date", presetIds: presetsForType("date").map((p) => p.id) },
    { groupKey: "columnFormat.group.custom", type: "custom", presetIds: ["number", "currency", "date"] },
  ];
}

export function presetsForType(type: ColumnFormatType): ColumnFormatPreset[] {
  return COLUMN_FORMAT_PRESETS.filter((p) => p.type === type);
}

export function findPreset(id: string | undefined): ColumnFormatPreset | undefined {
  if (!id || id === CUSTOM_FORMAT_PRESET_ID) return undefined;
  return COLUMN_FORMAT_PRESETS.find((p) => p.id === id);
}

export function parseColumnFormat(raw: unknown): ColumnFormat | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  const typeRaw = String(r.type || r.kind || "").trim().toLowerCase();
  if (typeRaw !== "number" && typeRaw !== "currency" && typeRaw !== "date") {
    return undefined;
  }
  const format: ColumnFormat = { type: typeRaw as ColumnFormatType };
  if (r.preset != null && String(r.preset).trim() !== "") {
    format.preset = String(r.preset).trim();
  }
  if (r.pattern != null && String(r.pattern).trim() !== "") {
    format.pattern = String(r.pattern).trim();
  }
  if (r.currency != null && String(r.currency).trim() !== "") {
    format.currency = String(r.currency).trim().toUpperCase();
  }
  if (r.locale != null && String(r.locale).trim() !== "") {
    format.locale = String(r.locale).trim();
  }
  return format;
}

export function columnFormatToRecord(format: ColumnFormat | undefined): Record<string, unknown> | undefined {
  if (!format) return undefined;
  const body: Record<string, unknown> = { type: format.type };
  if (format.preset) body.preset = format.preset;
  if (format.pattern) body.pattern = format.pattern;
  if (format.currency) body.currency = format.currency;
  if (format.locale) body.locale = format.locale;
  return body;
}

export function defaultFormatForFieldType(fieldType: string | undefined): ColumnFormat | undefined {
  const t = (fieldType || "").toLowerCase();
  if (t === "number" || t === "long" || t === "double" || t === "float" || t === "integer") {
    return { type: "number", preset: "decimal2" };
  }
  if (t === "date" || t.includes("date") || t === "datetime") {
    return { type: "date", preset: "shortDate" };
  }
  return undefined;
}

function resolveLocale(format: ColumnFormat, fallbackLocale?: string): string {
  if (format.locale) return format.locale;
  if (format.preset) {
    const preset = findPreset(format.preset);
    if (preset?.locale) return preset.locale;
  }
  return fallbackLocale || "tr-TR";
}

function formatNumberWithPattern(value: number, pattern: string, locale: string): string {
  const trimmed = pattern.trim();
  if (!trimmed) {
    return new Intl.NumberFormat(locale).format(value);
  }
  const decimalMatch = trimmed.match(/\.([0#]+)/);
  const fractionDigits = decimalMatch ? decimalMatch[1].replace(/[^0#]/g, "").length : 0;
  const useGrouping = trimmed.includes(",") || trimmed.includes(".");
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping,
  }).format(value);
}

function parseNumericValue(value: unknown): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "boolean") return value ? 1 : 0;
  const text = String(value).trim();
  if (!text) return null;
  const normalized = text.replace(/\s/g, "").replace(/,/g, ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseDateValue(value: unknown): dayjs.Dayjs | null {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value > 1e12 ? value : value * 1000;
    const d = dayjs(ms);
    return d.isValid() ? d : null;
  }
  const text = String(value).trim();
  if (!text) return null;
  if (/^\d+$/.test(text)) {
    const num = Number(text);
    const ms = num > 1e12 ? num : num * 1000;
    const d = dayjs(ms);
    if (d.isValid()) return d;
  }
  const iso = dayjs(text);
  if (iso.isValid()) return iso;
  const formats = [
    "YYYY-MM-DD",
    "YYYY-MM-DDTHH:mm:ss.SSSZ",
    "YYYY-MM-DDTHH:mm:ssZ",
    "DD.MM.YYYY HH:mm",
    "DD.MM.YYYY",
  ];
  for (const fmt of formats) {
    const d = dayjs(text, fmt, true);
    if (d.isValid()) return d;
  }
  return null;
}

export function formatColumnValue(
  value: unknown,
  format: ColumnFormat | undefined,
  fallbackLocale?: string
): string {
  if (value == null || value === "") return "";
  if (!format) {
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return "";
      }
    }
    return String(value);
  }

  const locale = resolveLocale(format, fallbackLocale);
  const preset = format.preset && format.preset !== CUSTOM_FORMAT_PRESET_ID ? findPreset(format.preset) : undefined;
  const usesCustom = format.preset === CUSTOM_FORMAT_PRESET_ID || (!preset && !!format.pattern);

  if (format.type === "date") {
    const date = parseDateValue(value);
    if (!date) return String(value);
    const pattern =
      (usesCustom ? format.pattern : undefined) ||
      preset?.datePattern ||
      format.pattern ||
      "YYYY-MM-DD";
    return date.format(pattern);
  }

  const num = parseNumericValue(value);
  if (num == null) return String(value);

  if (format.type === "currency") {
    if (usesCustom && format.pattern) {
      const formatted = formatNumberWithPattern(num, format.pattern, locale);
      const currency = format.currency || "TRY";
      return `${formatted} ${currency}`.trim();
    }
    const currency = format.currency || preset?.currency || "TRY";
    const currencyLocale = preset?.locale || locale;
    return new Intl.NumberFormat(currencyLocale, {
      style: "currency",
      currency,
      minimumFractionDigits: preset?.minimumFractionDigits ?? 2,
      maximumFractionDigits: preset?.maximumFractionDigits ?? 2,
    }).format(num);
  }

  if (usesCustom && format.pattern) {
    return formatNumberWithPattern(num, format.pattern, locale);
  }

  return new Intl.NumberFormat(locale, {
    style: preset?.style || "decimal",
    notation: preset?.notation,
    minimumFractionDigits: preset?.minimumFractionDigits,
    maximumFractionDigits: preset?.maximumFractionDigits,
  }).format(num);
}
